import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Lock, ShieldCheck } from "lucide-react";
import { formatUSD } from "@/lib/CatalogProvider";

/**
 * PricingSummary — premium animated summary card surfaced inside the inquiry form.
 *
 * Two states:
 *   1. Fixed-package (selectedPackage with base_price): shows base + addons + total.
 *   2. Custom scope (isCustomScope true): shows "Custom Quote Required" with budget hint.
 */
export const PricingSummary = ({
    selectedPackage,
    selectedAddons = [],
    basePrice,
    addOnsTotal,
    estimatedTotal,
    isCustomScope,
    customBudgetDisplay,
    accent = "gold", // "gold" | "cyan"
    testIdPrefix = "pricing-summary",
}) => {
    const isCool = accent === "cyan";
    const priceClass = isCool ? "pg-price-cyan" : "pg-price-gold";

    if (isCustomScope) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                className="rounded-2xl border p-5 sm:p-6"
                style={{
                    borderColor: isCool ? "rgba(111,227,221,0.35)" : "color-mix(in srgb, var(--pg-accent) 35%, var(--pg-border))",
                    background: isCool
                        ? "linear-gradient(135deg, rgba(43,169,163,0.10) 0%, rgba(8,15,16,0.55) 100%)"
                        : "linear-gradient(135deg, color-mix(in srgb, var(--pg-accent) 10%, transparent) 0%, var(--pg-surface) 100%)",
                }}
                data-testid={`${testIdPrefix}-custom`}
            >
                <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4" style={{ color: isCool ? "#6FE3DD" : "var(--pg-accent)" }} />
                    <span
                        className="text-[11px] font-medium tracking-normal"
                        style={{ color: isCool ? "rgba(168,240,236,0.85)" : "var(--pg-fg-muted)" }}
                    >
                        Pricing Summary · Custom Scope
                    </span>
                </div>
                <h3 className={`font-display mt-3 text-2xl sm:text-3xl ${priceClass}`}>
                    Custom Quote Required
                </h3>
                <p className="mt-3 text-sm leading-relaxed" style={{ color: isCool ? "rgba(186,222,226,0.85)" : "var(--pg-fg-muted)" }}>
                    Your scope is bespoke, pricing will be tailored to your final brief. We&rsquo;ll
                    confirm an exact quote within 24 hours of receiving your details.
                </p>
                {customBudgetDisplay && (
                    <div
                        className="mt-4 inline-flex items-center gap-2 rounded-full border px-3 py-1.5"
                        style={{
                            borderColor: isCool ? "rgba(168,240,236,0.35)" : "var(--pg-border-strong)",
                            color: isCool ? "#E9FBFE" : "var(--pg-fg)",
                            background: isCool ? "rgba(8,15,16,0.40)" : "var(--pg-surface-2)",
                        }}
                        data-testid={`${testIdPrefix}-custom-budget`}
                    >
                        <ShieldCheck className="h-3.5 w-3.5" style={{ color: isCool ? "#6FE3DD" : "var(--pg-accent)" }} />
                        <span className="text-[11px] font-medium tracking-normal">
                            Client budget: {customBudgetDisplay}
                        </span>
                    </div>
                )}
            </motion.div>
        );
    }

    if (!selectedPackage) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl border p-5 text-center"
                style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}
                data-testid={`${testIdPrefix}-empty`}
            >
                <span className="text-[11px] font-medium tracking-normal" style={{ color: "var(--pg-fg-muted)" }}>
                    Choose a package to see live pricing
                </span>
            </motion.div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-2xl border p-5 sm:p-6"
            style={{
                borderColor: isCool ? "rgba(111,227,221,0.35)" : "color-mix(in srgb, var(--pg-accent) 35%, var(--pg-border))",
                background: isCool
                    ? "linear-gradient(135deg, rgba(43,169,163,0.10) 0%, rgba(8,15,16,0.55) 100%)"
                    : "linear-gradient(135deg, color-mix(in srgb, var(--pg-accent) 8%, transparent) 0%, var(--pg-surface) 100%)",
            }}
            data-testid={testIdPrefix}
        >
            <div className="flex items-center gap-2">
                <Lock className="h-3.5 w-3.5" style={{ color: isCool ? "#6FE3DD" : "var(--pg-accent)" }} />
                <span
                    className="text-[11px] font-medium tracking-normal"
                    style={{ color: isCool ? "rgba(168,240,236,0.85)" : "var(--pg-fg-muted)" }}
                >
                    Pricing Summary · Live
                </span>
            </div>

            <div className="mt-4 flex flex-wrap items-baseline justify-between gap-3">
                <div>
                    <div
                        className="text-[11px] font-medium tracking-normal"
                        style={{ color: isCool ? "rgba(186,222,226,0.65)" : "var(--pg-fg-muted)" }}
                    >
                        Selected Package
                    </div>
                    <div className="font-display mt-1 text-lg sm:text-xl" style={{ color: isCool ? "#E9FBFE" : "var(--pg-fg)" }}>
                        {selectedPackage.name}
                    </div>
                </div>
                <div className="text-right">
                    <div
                        className="text-[11px] font-medium tracking-normal"
                        style={{ color: isCool ? "rgba(186,222,226,0.65)" : "var(--pg-fg-muted)" }}
                    >
                        Base Price
                    </div>
                    <div className="mt-1 text-xl sm:text-2xl" data-testid={`${testIdPrefix}-base`}>
                        <span className={`pg-price-num ${priceClass}`}>{formatUSD(basePrice)}</span>
                    </div>
                </div>
            </div>

            <div
                className="my-5 h-px"
                style={{
                    background: isCool
                        ? "linear-gradient(90deg, transparent, rgba(168,240,236,0.30), transparent)"
                        : "linear-gradient(90deg, transparent, var(--pg-border-strong), transparent)",
                }}
            />

            {/* Add-ons rows */}
            <AnimatePresence initial={false}>
                {selectedAddons.length === 0 ? (
                    <motion.p
                        key="empty-addons"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="text-xs"
                        style={{ color: isCool ? "rgba(186,222,226,0.60)" : "var(--pg-fg-muted)" }}
                        data-testid={`${testIdPrefix}-no-addons`}
                    >
                        No add-ons selected, tick add-ons below to enhance scope.
                    </motion.p>
                ) : (
                    <motion.ul
                        key="addon-list"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="space-y-1.5"
                        data-testid={`${testIdPrefix}-addons`}
                    >
                        {selectedAddons.map((a) => (
                            <motion.li
                                key={a.id}
                                layout
                                initial={{ opacity: 0, x: -6 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -6 }}
                                className="flex items-baseline justify-between text-sm"
                                style={{ color: isCool ? "#E9FBFE" : "var(--pg-fg)" }}
                            >
                                <span className="truncate pr-3">{a.name}</span>
                                <span className="font-mono text-xs" style={{ color: isCool ? "#A8F0EC" : "var(--pg-accent)" }}>
                                    +{formatUSD(a.price)}
                                </span>
                            </motion.li>
                        ))}
                    </motion.ul>
                )}
            </AnimatePresence>

            <div className="mt-4 flex items-baseline justify-between">
                <span
                    className="text-[11px] font-medium tracking-normal"
                    style={{ color: isCool ? "rgba(186,222,226,0.65)" : "var(--pg-fg-muted)" }}
                >
                    Add-ons Total
                </span>
                <span className="pg-price-num text-base" style={{ color: isCool ? "#E9FBFE" : "var(--pg-fg)" }} data-testid={`${testIdPrefix}-addons-total`}>
                    {formatUSD(addOnsTotal)}
                </span>
            </div>

            <div
                className="my-4 h-px"
                style={{
                    background: isCool
                        ? "linear-gradient(90deg, transparent, rgba(168,240,236,0.30), transparent)"
                        : "linear-gradient(90deg, transparent, var(--pg-border-strong), transparent)",
                }}
            />

            <div className="flex items-baseline justify-between">
                <span
                    className="text-[11px] font-medium tracking-normal"
                    style={{ color: isCool ? "#A8F0EC" : "var(--pg-accent)" }}
                >
                    Estimated Total
                </span>
                <motion.span
                    key={estimatedTotal}
                    initial={{ scale: 0.95 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
                    className={`pg-price-num text-3xl sm:text-4xl ${priceClass}`}
                    data-testid={`${testIdPrefix}-total`}
                >
                    {formatUSD(estimatedTotal)}
                </motion.span>
            </div>
        </motion.div>
    );
};

export default PricingSummary;
